import React, { useState, useRef, useEffect } from 'react';
import { PITLSummaryActionButton } from '../../components/ActionButtons';
import EditSection from '../../confirm/EditSection';
import SampleCollectionForm from '../../sample-collection/SampleCollectionForm';
import { fetchOmsRequisitions } from '../../util/apiUtils';
import { scrollToMessage } from '../../util/scrollToMessage';

/**
 * Creates an ActionButton that will display Sample Collection inputs
 */
export const SpecimenCollectionAction = ({ gearOrder, gearRequisition, userInfo, setGearOrder, setLoading }) => {
	const [showSampleCollectionForm, setShowSampleCollectionForm] = useState(false);
	const [isEditSaveInProgress, setIsEditSaveInProgress] = useState(false);
	const [sampleCollectionSaved, setSampleCollectionSaved] = useState(null);
	const [omsRequisition, setOmsRequisition] = useState(null);
	const [showButton] = useState(!gearRequisition?.dateLastSampleCollected);
	const sampleCollectionRef = useRef(null);
	const gearReqId = gearRequisition?.id;

	useEffect(() => {
		if (sampleCollectionSaved?.message) {
			scrollToMessage('#collection-error');
		}
	}, [sampleCollectionSaved]);

	// Scroll the sample collection form into view when it is opened
	useEffect(() => {
		if (showSampleCollectionForm) {
			const button = document.querySelector(`#collect_${gearReqId}`);
			if (button) {
				window.scrollTo(0, button.getBoundingClientRect().top + window.scrollY - 200);
			}
		}
	}, [showSampleCollectionForm, gearReqId]);

	// Load the omsRequisition object the first time we need to display Sample Collection inputs
	const hasOmsRequisition = !!omsRequisition;
	useEffect(() => {
		if (showSampleCollectionForm && !hasOmsRequisition) {
			if (gearReqId) {
				setLoading(true);
				fetchOmsRequisitions([gearReqId])
					.then((json) => {
						setOmsRequisition(json);
					})
					.catch((e) => {
						// Close the form so opening it back up will trigger a new omsRequisitions fetch
						setShowSampleCollectionForm(false);
						setSampleCollectionSaved({
							status: 'error',
							message:
								'An unexpected error has occurred. Please try again or call us at 800-522-4762 if the issue continues.'
						});
					})
					.finally(() => {
						setLoading(false);
					});
			} else {
				// Nothing to load. Set omsRequisitions as an empty array so we don't hit this code again
				setOmsRequisition([]);
			}
		}
	}, [gearReqId, hasOmsRequisition, setLoading, showSampleCollectionForm]);

	/**
	 * Toggle display of the Sample Collection Form
	 */
	const toggleSampleCollectionForm = () => {
		setShowSampleCollectionForm((prevState) => !prevState);
		setSampleCollectionSaved(null);
	};

	/**
	 * Saves the values of the Sample Collection Form.
	 *
	 * The Sample Collection Form will handle its own validation and save by calling methods on the sampleCollectionRef.
	 * When validation fails we expect a rejected Promise with the string 'ValidationError'.
	 * When a save is successful we expect a resolved Promise with the updated GearOrder object.
	 * Any other rejected Promise is unexpected and will result in an error message being displayed.
	 */
	const saveEdit = () => {
		// isEditSaveInProgress will trigger a shield and spinner icon to overlay the section being edited.
		setIsEditSaveInProgress(true);

		sampleCollectionRef.current
			.save()
			.then((gearOrder) => {
				setShowSampleCollectionForm(false);
				setSampleCollectionSaved({ status: 'success' });
				setGearOrder(gearOrder);
			})
			.catch((err) => {
				if (err === 'ValidationError') {
					// Do nothing. The Sample Collection Form will show the validation errors.
				} else {
					setSampleCollectionSaved({
						status: 'error',
						message:
							'An unexpected error has occurred. Please try again or call us at 800-522-4762 if the issue continues.'
					});
				}
			})
			.finally(() => {
				setIsEditSaveInProgress(false);
			});
	};

	return (
		<>
			{showButton && (
				<span id={`collect_${gearRequisition.id}`}>
					<PITLSummaryActionButton title="Enter Specimen Details" onClick={toggleSampleCollectionForm} />
				</span>
			)}

			{showSampleCollectionForm && (
				<div style={{ paddingTop: '10px' }}>
					<EditSection
						title="Sample Collection Information"
						onCancel={toggleSampleCollectionForm}
						onSave={saveEdit}
						isEditSaveInProgress={isEditSaveInProgress}
					>
						<SampleCollectionForm
							ref={sampleCollectionRef}
							gearRequisition={gearRequisition}
							omsRequisition={omsRequisition?.[0]}
							gearOrder={gearOrder}
							userType={userInfo?.type}
						/>
					</EditSection>
				</div>
			)}
			{sampleCollectionSaved?.status === 'success' && (
				<h3 style={{ display: 'inline-block' }}>
					<span className="ms-2 text-success">
						Changes saved<i className="ms-2 fa fa-check-circle"></i>
					</span>
				</h3>
			)}
			{sampleCollectionSaved?.status === 'error' && sampleCollectionSaved.message && (
				<div id="collection-error" className="alert alert-danger bold">
					{sampleCollectionSaved.message}
				</div>
			)}
		</>
	);
};
